/**
 * #472 — reads over the relay's Oscar blocker registry.
 *
 * Two consumers, one wire source (`/api/kalshi/oscar/blockers*`):
 *
 *   useOscarKillPreview — the ticket's pre-fire warning. Asks the relay which
 *   friendly blockers the previewed take would cross (the SAME query the
 *   sequence route uses to derive kill legs), so the click preview shows
 *   exactly what would fire — "KILL <name>'s blocker @ x¢". Debounced; a
 *   response for a superseded (scope, ticker, side, price, quantity) is dropped.
 *
 *   useOscarOvertakenNotices — the "your blocker was overtaken" ghost rows.
 *   A killed blocker simply disappears from Open Orders, so this polls the
 *   trader's own registry rows and surfaces recent `overtaken` transitions
 *   with the relay-resolved self/teammate ownership class.
 *
 * Ownership class is RELAY-supplied throughout — the frontend never infers
 * another trader's identity from local state (oscarResultPresentation.ts contract).
 * Every fetch failure lands as "no data", never a throw.
 */
import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '@/stores/authStore';

export interface OscarKillPreviewBlocker {
  own: boolean;
  takePriceCents: number;
  blockerPriceCents: number;
  count: number;
}

export interface OscarKillPreview {
  killMode: 'off' | 'shadow' | 'on';
  blockers: OscarKillPreviewBlocker[];
}

function parseOscarKillPreview(value: unknown): OscarKillPreview | null {
  if (value == null || typeof value !== 'object') return null;
  const candidate = value as Partial<OscarKillPreview>;
  if (!['off', 'shadow', 'on'].includes(candidate.killMode ?? '')) return null;
  if (!Array.isArray(candidate.blockers)) return null;
  if (
    !candidate.blockers.every(
      (blocker) =>
        blocker != null &&
        typeof blocker === 'object' &&
        typeof blocker.own === 'boolean' &&
        Number.isFinite(blocker.takePriceCents) &&
        Number.isFinite(blocker.blockerPriceCents) &&
        Number.isFinite(blocker.count),
    )
  ) {
    return null;
  }
  return candidate as OscarKillPreview;
}

interface KeyedValue<T> {
  key: string;
  value: T;
}

const PREVIEW_DEBOUNCE_MS = 400;

export function useOscarKillPreview(args: {
  enabled: boolean;
  /** Local authenticated user/org identity only. Never sent to the relay. */
  scopeKey: string | null;
  ticker: string | null;
  side: 'yes' | 'no' | null;
  priceCents: number | null;
  quantity: number | null;
}): OscarKillPreview | null {
  const { enabled, scopeKey, ticker, side, priceCents, quantity } = args;
  const previewKey =
    enabled &&
    scopeKey &&
    ticker &&
    side &&
    priceCents != null &&
    Number.isInteger(priceCents) &&
    priceCents >= 1 &&
    priceCents <= 98 &&
    quantity != null &&
    Number.isFinite(quantity) &&
    quantity > 0
      ? JSON.stringify([scopeKey, ticker, side, priceCents, quantity])
      : null;
  const [previewState, setPreviewState] = useState<KeyedValue<OscarKillPreview | null>>({
    key: '',
    value: null,
  });
  const generation = useRef(0);

  useEffect(() => {
    const gen = ++generation.current;
    if (previewKey == null || !ticker || !side || priceCents == null) return;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const qs = new URLSearchParams({
            ticker,
            side,
            priceCents: String(priceCents),
          });
          const res = await apiFetch(`/api/kalshi/oscar/blockers/at-level?${qs}`);
          if (!res.ok) {
            if (generation.current === gen) {
              setPreviewState({ key: previewKey, value: null });
            }
            return;
          }
          const body = parseOscarKillPreview(await res.json());
          if (generation.current === gen) {
            setPreviewState({ key: previewKey, value: body });
          }
        } catch {
          if (generation.current === gen) {
            setPreviewState({ key: previewKey, value: null });
          }
        }
      })();
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [previewKey, ticker, side, priceCents]);

  return previewKey != null && previewState.key === previewKey ? previewState.value : null;
}

export interface OscarOvertakenNotice {
  kalshiOrderId: string;
  ticker: string;
  takeSide: 'yes' | 'no';
  takePriceCents: number;
  blockerSide: 'yes' | 'no';
  blockerPriceCents: number;
  count: number;
  killedByClass: 'self' | 'teammate' | null;
  updatedAt: string;
}

interface RegistryBlockerWire extends OscarOvertakenNotice {
  status: string;
}

const NOTICE_POLL_MS = 15_000;
const NOTICE_WINDOW_MS = 15 * 60 * 1_000;

/**
 * Recent `overtaken` registry rows for tickers under any of the given
 * prefixes (a game's Kalshi event prefixes). Empty array while disabled,
 * loading, or when there is nothing to show.
 */
export function useOscarOvertakenNotices(args: {
  enabled: boolean;
  tickerPrefixes: string[];
}): OscarOvertakenNotice[] {
  const { enabled, tickerPrefixes } = args;
  const [noticeState, setNoticeState] = useState<KeyedValue<OscarOvertakenNotice[]>>({
    key: '',
    value: [],
  });
  // Join for the effect key so a stable set of prefixes doesn't re-poll.
  const prefixKey = tickerPrefixes.join('|');

  useEffect(() => {
    if (!enabled || prefixKey === '') return;
    let disposed = false;
    const prefixes = prefixKey.split('|');
    const load = async () => {
      try {
        const res = await apiFetch('/api/kalshi/oscar/blockers');
        if (!res.ok) return;
        const body = (await res.json()) as { blockers: RegistryBlockerWire[] };
        if (disposed) return;
        const cutoff = Date.now() - NOTICE_WINDOW_MS;
        setNoticeState({
          key: prefixKey,
          value: body.blockers.filter(
            (b) =>
              b.status === 'overtaken' &&
              new Date(b.updatedAt).getTime() >= cutoff &&
              prefixes.some((p) => b.ticker.startsWith(p)),
          ),
        });
      } catch {
        // keep last known notices; next poll retries
      }
    };
    void load();
    const timer = setInterval(() => void load(), NOTICE_POLL_MS);
    return () => {
      disposed = true;
      clearInterval(timer);
    };
  }, [enabled, prefixKey]);

  return enabled && prefixKey !== '' && noticeState.key === prefixKey ? noticeState.value : [];
}

/** `KXMLBGAME-26JUL30NYYBAL-NYY` → `KXMLBGAME-26JUL30NYYBAL-` — the event
 *  prefix every market of the same game shares. Null for null/blank input. */
export function kalshiEventPrefix(marketTicker: string | null | undefined): string | null {
  if (!marketTicker) return null;
  const lastDash = marketTicker.lastIndexOf('-');
  return lastDash > 0 ? marketTicker.slice(0, lastDash + 1) : null;
}
